<script setup lang="ts">
defineProps({
  isSearchActive: {
    type: Boolean,
    required: true,
  },
  isFiltersActive: {
    type: Boolean,
    required: true,
  },
  isMobile: {
    type: Boolean,
    required: true,
  },
  searchQuery: {
    type: String,
    required: true,
  },
  selectedCategories: {
    type: Array,
    required: true,
  },
  selectedProviders: {
    type: Array,
    required: true,
  },
  categoriesList: {
    type: Array,
    required: true,
    default: () => [],
  },
  providersList: {
    type: Array,
    required: true,
  },
});

type Emits = {
  'update:searchQuery': [any];
  'update:selectedProviders': [any];
  'update:selectedCategories': [any];
};

const emit = defineEmits<Emits>();

const route = useRoute();
const categorySlug = computed(() => {
  // @ts-ignore
  return route.params.category?.length ? route.params.category[0] : '';
});

const providerSlug = computed<string>(() => {
  // @ts-ignore
  const { provider } = route.params;

  if (provider) {
    return String(provider);
  }

  return '';
});

const conditionalGridClasses = computed(() => {
  if (
    providerSlug.value ||
    (categorySlug.value && categorySlug.value !== 'all')
  )
    return '';
  else return 'grid grid-cols-2 gap-4';
});
</script>

<template>
  <div
    class="casino-filters"
    :class="
      isMobile && (isSearchActive || isFiltersActive)
        ? 'casino-filters-open'
        : ''
    "
  >
    <div
      v-if="isSearchActive || !isMobile"
      class="casino-search"
    >
      <input
        :placeholder="$t('casino.game.header.search_placeholder')"
        :value="searchQuery"
        @input="
          (event: Event) =>
            emit(
              'update:searchQuery',
              (event.target as HTMLInputElement)?.value,
            )
        "
        @keyup.enter="hideMobileKeyboard($event.target)"
      />
    </div>

    <div
      v-if="isFiltersActive || !isMobile"
      class="casino-filters__buttons"
      :class="conditionalGridClasses"
    >
      <HeadlessListbox
        v-if="!providerSlug"
        :model-value="selectedProviders"
        multiple
        @update:model-value="
          (value: string) => emit('update:selectedProviders', value)
        "
      >
        <div class="listBox-wrapper">
          <HeadlessListboxButton class="select-btn">
            {{ $t('casino.game.header.providers_button') }}
            <strong>{{
              selectedProviders && selectedProviders?.length
                ? selectedProviders.length
                : $t('casino.game.header.all')
            }}</strong>
          </HeadlessListboxButton>
          <HeadlessListboxOptions class="select-options no-scrollbar">
            <HeadlessListboxOption
              v-for="(opt, i) in providersList as any"
              :key="i"
              class="select-opt"
              :value="opt.slug"
            >
              <label :for="opt.slug">
                <input
                  :id="`${opt.slug}-${i}`"
                  :checked="selectedProviders.includes(opt.slug)"
                  name="opt"
                  type="checkbox"
                  :value="opt.slug"
                />
                <span class="ml-2">{{ opt.name }}</span>
                <span class="badge">
                  {{ opt.games }}
                </span>
              </label>
            </HeadlessListboxOption>
          </HeadlessListboxOptions>
        </div>
      </HeadlessListbox>
      <HeadlessListbox
        v-if="!categorySlug || categorySlug === 'all'"
        :model-value="selectedCategories?.[0] || ''"
        @update:model-value="
          (value: string) =>
            emit(
              'update:selectedCategories',
              selectedCategories?.includes(value) ? [] : [value],
            )
        "
      >
        <div class="listBox-wrapper">
          <HeadlessListboxButton class="select-btn">
            {{ $t('casino.game.header.categories_button') }}
            <strong>{{
              selectedCategories && selectedCategories?.length
                ? selectedCategories.length
                : $t('casino.game.header.all')
            }}</strong>
          </HeadlessListboxButton>
          <HeadlessListboxOptions class="select-options no-scrollbar">
            <HeadlessListboxOption
              v-for="(opt, i) in categoriesList as any"
              :key="i"
              class="select-opt"
              :value="opt.slug"
            >
              <label :for="opt.slug">
                <input
                  :id="`${opt.slug}-${i}`"
                  :checked="selectedCategories.includes(opt.slug)"
                  name="category"
                  type="radio"
                  :value="opt.slug"
                />
                <span class="ml-2">
                  {{ $t(`${opt.name}`) }}
                </span>
                <span class="badge">
                  {{ opt.games }}
                </span>
              </label>
            </HeadlessListboxOption>
          </HeadlessListboxOptions>
        </div>
      </HeadlessListbox>
    </div>
  </div>
</template>

<style scoped lang="scss">
.casino-filters {
  @apply flex items-center justify-between lg:justify-end;

  .casino-search {
    @apply bg-white/10 text-white rounded px-2 w-full md:mr-4 lg:w-40 xl:w-56;

    input {
      @apply w-full text-xs xl:text-[.8rem];
    }
    input::placeholder {
      @apply text-texts/30 text-xs xl:text-[.8rem];
    }
  }
}

.casino-filters-open {
  @apply mt-5;
}

.casino-filters__buttons {
  @apply w-full lg:w-auto;
}

.listBox-wrapper {
  @apply relative w-full z-[60] lg:w-40;

  .select-btn {
    @apply relative w-full cursor-pointer rounded bg-texts/20 text-white truncate text-ellipsis;
    @apply outline-none py-2 px-4 text-left text-xs font-semibold xl:text-[.8rem];
    @apply hover:bg-primary/10 hover:text-primary;

    strong {
      @apply font-extrabold;
    }
  }

  .select-options {
    @apply absolute mt-1 max-h-60 w-full cursor-pointer overflow-auto rounded bg-bg-secondary text-white;
    @apply shadow-lg focus:outline-none text-xs;
  }

  .select-opt {
    @apply p-2 whitespace-nowrap;
    @apply hover:bg-primary/10 hover:text-primary;

    label {
      @apply flex items-center;
    }

    /* stylelint-disable-next-line no-descending-specificity */
    input {
      @apply rounded;
    }

    .badge {
      @apply block ml-2 bg-white/40 text-black rounded p-0.5 text-[.6rem] font-semibold leading-none;
    }
  }
}
</style>
